import { pressTravelled, type PressPoint } from './touch-press.js'

/**
 * The first tap while the keyboard is up (iPhone app sweep, 19 Oct 2026). Tapping a button while a
 * box has the keyboard open blurs the box, the keyboard starts to close and the page grows back
 * under the finger; in the installed app the click that should follow the tap sometimes never
 * comes ("Next: add fields" did nothing the first time). This watches exactly that case — a touch
 * on a button or link that starts while a text box has focus — and, when the browser's own click
 * does not arrive shortly after the finger lifts, clicks the control itself. A late native click
 * after that is dropped, so the control never runs twice.
 */

/** How long after the finger lifts the browser's own click may take before the rescue clicks. */
export const RESCUE_DELAY_MS = 350

/** How long a late native click on a rescued control is still treated as the same tap. */
export const RESCUE_DEDUPE_MS = 1000

/** Controls a tap activates. A text box itself is never one: tapping it moves the caret. */
export const ACTIVATABLE_SELECTOR =
  'button, a[href], summary, [role="button"], [role="tab"], [role="menuitem"], [role="option"], [role="link"], input[type="checkbox"], input[type="radio"], input[type="submit"], input[type="button"]'

const TEXT_INPUT_TYPES = new Set([
  '',
  'text',
  'search',
  'email',
  'tel',
  'url',
  'password',
  'number',
  'date',
  'datetime-local',
  'month',
  'time',
  'week',
])

/** The parts of an element this reads, so the rules can be tested without a DOM. */
export interface ElementLike {
  readonly tagName: string
  readonly type?: string
  readonly isContentEditable?: boolean
}

/** Whether a focused element raises the on-screen keyboard. */
export function raisesKeyboard(element: ElementLike | null | undefined): boolean {
  if (element === null || element === undefined) return false
  const tag = element.tagName.toUpperCase()
  if (tag === 'TEXTAREA') return true
  if (tag === 'INPUT') return TEXT_INPUT_TYPES.has((element.type ?? '').toLowerCase())
  return element.isContentEditable === true
}

/** One tap being watched: the control under the finger and where the finger came down. */
interface WatchedTap<Target> {
  readonly target: Target
  readonly pointer: number
  readonly start: PressPoint
}

/**
 * The rescue's decisions, free of the DOM: the installer feeds it pointer and click events and
 * runs what it returns. `contains(a, b)` says whether `b` is `a` or inside it.
 */
export class KeyboardTapRescue<Target> {
  private watched: WatchedTap<Target> | null = null
  private awaiting: Target | null = null
  private rescued: { readonly target: Target; readonly until: number } | null = null

  constructor(private readonly contains: (outer: Target, inner: Target) => boolean) {}

  /** A finger came down on `control` (null: not a control) while `keyboardUp`. */
  down(control: Target | null, keyboardUp: boolean, pointer: number, at: PressPoint): void {
    this.awaiting = null
    this.watched = control !== null && keyboardUp ? { target: control, pointer, start: at } : null
  }

  move(pointer: number, at: PressPoint): void {
    if (this.watched?.pointer === pointer && pressTravelled(this.watched.start, at)) {
      this.watched = null
    }
  }

  cancel(): void {
    this.watched = null
    this.awaiting = null
  }

  /** The finger lifted: true when the installer should start the rescue timer. */
  up(pointer: number, at: PressPoint): boolean {
    const watched = this.watched
    this.watched = null
    if (watched === null || watched.pointer !== pointer || pressTravelled(watched.start, at)) {
      return false
    }
    this.awaiting = watched.target
    return true
  }

  /**
   * A click reached the page. Returns `drop` when it is a late native click on a control the rescue
   * already clicked, so the installer swallows it.
   */
  click(target: Target, trusted: boolean, now: number): 'keep' | 'drop' {
    if (this.awaiting !== null && this.contains(this.awaiting, target)) this.awaiting = null
    const rescued = this.rescued
    if (
      rescued !== null &&
      trusted &&
      now <= rescued.until &&
      this.contains(rescued.target, target)
    ) {
      this.rescued = null
      return 'drop'
    }
    return 'keep'
  }

  /** The timer ran out: the control to click, or null when the browser's own click came. */
  timeout(now: number): Target | null {
    const target = this.awaiting
    this.awaiting = null
    if (target === null) return null
    this.rescued = { target, until: now + RESCUE_DEDUPE_MS }
    return target
  }
}

/** Installs the rescue on a document; returns its removal. */
export function installKeyboardTapRescue(doc: Document = document): () => void {
  const view = doc.defaultView
  if (view === null) return () => undefined
  const rescue = new KeyboardTapRescue<Element>((outer, inner) => outer.contains(inner))
  let timer = 0
  const point = (event: PointerEvent): PressPoint => ({ x: event.clientX, y: event.clientY })

  const onDown = (event: PointerEvent): void => {
    if (event.pointerType !== 'touch') return
    const target = event.target instanceof Element ? event.target : null
    const control = target?.closest(ACTIVATABLE_SELECTOR) ?? null
    const active = doc.activeElement as (Element & ElementLike) | null
    const keyboardUp = raisesKeyboard(active) && control !== null && !control.contains(active)
    rescue.down(control, keyboardUp, event.pointerId, point(event))
  }
  const onMove = (event: PointerEvent): void => rescue.move(event.pointerId, point(event))
  const onUp = (event: PointerEvent): void => {
    if (!rescue.up(event.pointerId, point(event))) return
    view.clearTimeout(timer)
    timer = view.setTimeout(() => {
      const target = rescue.timeout(Date.now())
      if (target instanceof HTMLElement && target.isConnected) target.click()
    }, RESCUE_DELAY_MS)
  }
  const onCancel = (): void => rescue.cancel()
  const onClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return
    if (rescue.click(event.target, event.isTrusted, Date.now()) === 'drop') {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  doc.addEventListener('pointerdown', onDown, true)
  doc.addEventListener('pointermove', onMove, true)
  doc.addEventListener('pointerup', onUp, true)
  doc.addEventListener('pointercancel', onCancel, true)
  doc.addEventListener('click', onClick, true)
  return () => {
    view.clearTimeout(timer)
    doc.removeEventListener('pointerdown', onDown, true)
    doc.removeEventListener('pointermove', onMove, true)
    doc.removeEventListener('pointerup', onUp, true)
    doc.removeEventListener('pointercancel', onCancel, true)
    doc.removeEventListener('click', onClick, true)
  }
}
